import { ArrowRight, Bot, Check, Clock3, Eye, FileSearch, Radio, RefreshCw, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, api, formatRelative } from "../api";
import { useAuth } from "../auth";
import { Button, CopyButton } from "../components";
import { providerLabel, useClaimPreview } from "./claimPreview";

const SHOWN_LOOPS = 6;

/**
 * The live receiver as it sits beside the prompt on the Start page: listens for the agent, shows the
 * loops that arrived, and offers the claim. Everything it shows came from a real sync.
 */
export type ReceiverState = "waiting" | "ready" | "claimed" | "expired";

export function LiveReceiver({ token, claimUrl, onStateChange }: { token: string; claimUrl: string; onStateChange?: (state: ReceiverState) => void }) {
  const { preview, error, reload } = useClaimPreview(token);
  useEffect(() => {
    if (preview?.state) onStateChange?.(preview.state);
  }, [preview?.state, onStateChange]);
  const { user, loading: authLoading, refresh } = useAuth();
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");
  const navigate = useNavigate();
  const agent = providerLabel(preview?.provider);
  const state = preview?.state ?? "waiting";
  const count = preview?.totals.loops ?? 0;

  async function claim() {
    setClaiming(true);
    setClaimError("");
    try {
      const result = await api<{ organizationId: string }>("/api/app/claim", { method: "POST", body: JSON.stringify({ token }) });
      localStorage.setItem("lg.organization", result.organizationId);
      sessionStorage.removeItem("lg.pendingClaim");
      await refresh();
      navigate("/app", { replace: true });
    } catch (caught) {
      setClaimError(caught instanceof ApiError ? caught.message : "This catalog could not be claimed");
    } finally {
      setClaiming(false);
    }
  }

  if (state === "claimed" || state === "expired") {
    return (
      <aside className={`receiver-panel is-${state}`} aria-live="polite">
        <div className="receiver-panel__head"><span><Radio size={14} aria-hidden="true" /> Live receiver</span><i>{state === "claimed" ? "Claimed" : "Expired"}</i></div>
        <div className="receiver-panel__terminal">
          {state === "claimed" ? <Check size={26} aria-hidden="true" /> : <Clock3 size={26} aria-hidden="true" />}
          <h2>{state === "claimed" ? "This catalog is already yours." : "This receiver expired."}</h2>
          <p>{state === "claimed" ? "Ownership is attached. Ongoing sync and run reporting are still off until you turn them on." : "Unclaimed catalogs are kept for 14 days. Create a new prompt to start again."}</p>
          <Link className="button button--primary" to={state === "claimed" ? "/app" : "/start"} onClick={() => sessionStorage.removeItem("lg.pendingClaim")}>{state === "claimed" ? "Open your loops" : "Start again"} <ArrowRight size={15} aria-hidden="true" /></Link>
        </div>
      </aside>
    );
  }

  const ready = state === "ready";
  return (
    <aside className={`receiver-panel ${ready ? "is-ready" : "is-waiting"}`} aria-live="polite">
      <div className="receiver-panel__head">
        <span><Radio size={14} aria-hidden="true" /> Live receiver</span>
        <span className="receiver-panel__head-right"><i>{ready ? "Ready" : "Waiting"}</i><button type="button" className="icon-button" onClick={() => void reload()} aria-label="Refresh the receiver"><RefreshCw size={14} aria-hidden="true" /></button></span>
      </div>

      <div className="receiver-panel__pulse" aria-hidden="true"><i /><i /><span>{count}</span><small>{count === 1 ? "loop found" : "loops found"}</small></div>

      <p className="eyebrow">{ready ? "Step 03 · Claim your loops" : `Step 02 · Awaiting ${agent}`}</p>
      <h2>{ready ? `${count} ${count === 1 ? "loop" : "loops"} arrived.` : `Listening for ${agent}.`}</h2>
      <p className="receiver-panel__lede">
        {ready
          ? `Review what ${agent} could substantiate, then attach the catalog to your identity. Nothing was edited or scheduled.`
          : `Paste the prompt into ${agent}. The catalog forms here the moment it syncs. You can close this page and come back with the link below.`}
      </p>

      {error && <div className="form-error journey-inline-error" role="alert"><strong>Receiver refresh failed.</strong><span>{error}</span><small>The last confirmed state stays shown.</small></div>}

      {ready && preview ? (
        <ol className="receiver-panel__loops">
          {preview.loops.slice(0, SHOWN_LOOPS).map((loop) => (
            <li key={loop.id}><b>{loop.name}</b><small>{loop.confidence || "observed"} · {loop.cadence || "cadence unknown"}</small></li>
          ))}
          {preview.loops.length > SHOWN_LOOPS && <li className="receiver-panel__more">+{preview.loops.length - SHOWN_LOOPS} more in the catalog</li>}
        </ol>
      ) : (
        <ul className="receiver-panel__facts">
          <li><FileSearch size={15} aria-hidden="true" /><span><b>Reads accessible configuration</b><small>Schedules, task lists, workspace instructions, repeatable work.</small></span></li>
          <li><Eye size={15} aria-hidden="true" /><span><b>Sends metadata and evidence</b><small>Name, purpose, cadence, source, confidence. Never private content.</small></span></li>
          <li><ShieldCheck size={15} aria-hidden="true" /><span><b>Changes nothing</b><small>No loop edits, no observer, no schedule, no invented outcomes.</small></span></li>
        </ul>
      )}

      <div className="receiver-panel__actions">
        {ready ? (
          authLoading ? <Button busy className="button--full">Checking identity</Button> : user ? (
            <Button onClick={() => void claim()} busy={claiming} className="button--full">Claim as {user.displayName} <ArrowRight size={15} aria-hidden="true" /></Button>
          ) : (
            <>
              <Link className="button button--primary button--full" to={`/claim/signup#${token}`}>Create account and claim <ArrowRight size={15} aria-hidden="true" /></Link>
              <Link className="button button--secondary button--full" to={`/claim/signin#${token}`}>I already have an account</Link>
            </>
          )
        ) : (
          <div className="receiver-panel__return">
            <span><b>Come back later</b><small>This link reopens the receiver. It is yours alone; the agent never sees it.</small></span>
            <CopyButton value={claimUrl} label="Copy receiver link" />
          </div>
        )}
        {claimError && <div className="form-error" role="alert">{claimError}</div>}
      </div>

      <footer className="receiver-panel__foot">
        <Bot size={13} aria-hidden="true" />
        <span>{ready && preview?.lastSyncedAt ? `Last sync ${formatRelative(preview.lastSyncedAt)} · still private` : "The agent never receives this link, your email, or your passkey."}</span>
      </footer>
    </aside>
  );
}
